// src/services/agentService.ts
import axios from "axios"
import { supabase } from "../supabaseClient"

/** Axios instance pointing to our server API */
const api = axios.create({
  baseURL: process.env.REACT_APP_SERVER_URL ? `${process.env.REACT_APP_SERVER_URL}/api/voice` : "/api/voice",
  headers: {
    "Content-Type": "application/json",
  },
})

api.interceptors.request.use(
  async (config) => {
    // Get the current session
    const {
      data: { session },
    } = await supabase.auth.getSession()

    // If there's a session, add the user ID to the headers
    if (session) {
      config.headers["user-id"] = session.user.id

      const { data: profile } = await supabase.from("profiles").select("role").eq("id", session.user.id).single()

      if (profile && ["admin","account_admin","super_admin"].includes(profile.role)) {
        const selectedCompanyName = localStorage.getItem("selectedCompanyName")
        if (selectedCompanyName) {
          config.headers["x-company-name"] = selectedCompanyName
          console.log("[v0] Voice Service - Setting x-company-name header:", selectedCompanyName)
        }
      }
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

export type AgentResponseEngine =
  | { type: "retell-llm"; llm_id: string; version?: number | null }
  | { type: "custom-llm"; llm_websocket_url: string }

// Update the Agent interface
export interface Agent {
  agent_id: string
  agent_name: string | null
  last_modification_timestamp: number
  is_active?: boolean

  // Retell response fields (optional)
  version?: number
  is_published?: boolean
  version_description?: string | null
  is_public?: boolean | null

  response_engine: AgentResponseEngine

  voice_id: string
  voice_model:
    | "eleven_turbo_v2"
    | "eleven_flash_v2"
    | "eleven_turbo_v2_5"
    | "eleven_flash_v2_5"
    | "eleven_multilingual_v2"
    | "eleven_v3"
    | "sonic-2"
    | "sonic-3"
    | "sonic-3-latest"
    | "sonic-turbo"
    | "tts-1"
    | "gpt-4o-mini-tts"
    | "speech-02-turbo"
    | null
  language:
    | "en-US"
    | "en-IN"
    | "en-GB"
    | "en-AU"
    | "en-NZ"
    | "de-DE"
    | "es-ES"
    | "es-419"
    | "hi-IN"
    | "fr-FR"
    | "fr-CA"
    | "ja-JP"
    | "pt-PT"
    | "pt-BR"
    | "zh-CN"
    | "ru-RU"
    | "it-IT"
    | "ko-KR"
    | "nl-NL"
    | "nl-BE"
    | "pl-PL"
    | "tr-TR"
    | "vi-VN"
    | "ro-RO"
    | "bg-BG"
    | "ca-ES"
    | "th-TH"
    | "da-DK"
    | "fi-FI"
    | "el-GR"
    | "hu-HU"
    | "id-ID"
    | "no-NO"
    | "sk-SK"
    | "sv-SE"
    | "lt-LT"
    | "lv-LV"
    | "ms-MY"
    | "af-ZA"
    | "ar-SA"
    | "az-AZ"
    | "bs-BA"
    | "cy-GB"
    | "fa-IR"
    | "fil-PH"
    | "gl-ES"
    | "he-IL"
    | "hr-HR"
    | "hy-AM"
    | "is-IS"
    | "kk-KZ"
    | "kn-IN"
    | "mk-MK"
    | "mr-IN"
    | "ne-NP"
    | "sl-SI"
    | "sr-RS"
    | "sw-KE"
    | "ta-IN"
    | "ur-IN"
    | "yue-CN"
    | "uk-UA"
    | "multi"

  webhook_url?: string | null
  webhook_timeout_ms?: number
  agent_webhook_url?: string | null

  data_storage_setting?: "everything" | "everything_except_pii" | "basic_attributes_only"
  opt_in_signed_url?: boolean
  signed_url_expiration_ms?: number | null

  fallback_voice_ids?: string[] | null

  // Speech settings
  ambient_sound?: "coffee-shop" | "convention-hall" | "summer-outdoor" | "mountain-outdoor" | "static-noise" | "call-center" | null
  ambient_sound_volume?: number
  responsiveness?: number
  interruption_sensitivity?: number
  enable_backchannel?: boolean
  backchannel_frequency?: number
  backchannel_words?: string[] | null
  boosted_keywords?: string[] | null
  normalize_for_speech?: boolean
  reminder_trigger_ms?: number
  reminder_max_count?: number
  pronunciation_dictionary?: Array<{
    word: string
    alphabet: "ipa" | "cmu"
    phoneme: string
  }> | null

  // STT / DTMF / Audio cleanup
  stt_mode?: "fast" | "accurate" | "custom"
  custom_stt_config?: {
    provider: "azure" | "deepgram"
    endpointing_ms: number
  }
  vocab_specialization?: "general" | "medical"
  denoising_mode?: "noise-cancellation" | "noise-and-background-speech-cancellation"
  allow_user_dtmf?: boolean
  user_dtmf_options?: {
    digit_limit?: number | null
    termination_key?: string | null
    timeout_ms?: number
  } | null

  // PII scrubbing config
  pii_config?: {
    mode: "post_call"
    categories: Array<
      | "person_name"
      | "address"
      | "email"
      | "phone_number"
      | "ssn"
      | "passport"
      | "driver_license"
      | "credit_card"
      | "bank_account"
      | "password"
      | "pin"
      | "medical_id"
      | "date_of_birth"
      | "customer_account_number"
    >
  }

  // Call lifecycle
  begin_message_delay_ms?: number
  ring_duration_ms?: number
  end_call_after_silence_ms?: number
  max_call_duration_ms?: number

  // Voicemail / IVR
  enable_voicemail_detection?: boolean
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
  voicemail_option?: VoicemailOption | null
  ivr_option?: { action: { type: "hangup" } } | null

  // Post-call analysis
  post_call_analysis_data?: Array<{
    type: "string" | "enum" | "boolean" | "number"
    name: string
    description: string
    examples?: string[]
    choices?: string[]
  }> | null
  post_call_analysis_model?:
    | "gpt-4.1"
    | "gpt-4.1-mini"
    | "gpt-4.1-nano"
    | "gpt-5"
    | "gpt-5.1"
    | "gpt-5.2"
    | "gpt-5-mini"
    | "gpt-5-nano"
    | "claude-4.5-sonnet"
    | "claude-4.5-haiku"
    | "gemini-2.5-flash"
    | "gemini-2.5-flash-lite"
    | "gemini-3.0-flash"
    | null
  analysis_successful_prompt?: string | null
  analysis_summary_prompt?: string | null

  // Voice settings
  voice_speed?: number
  voice_temperature?: number
  volume?: number
  enable_dynamic_voice_speed?: boolean
}

// Update the CreateAgentParams interface
export interface CreateAgentParams {
  agent_name?: string
  version_description?: string | null
  is_public?: boolean | null

  response_engine: AgentResponseEngine

  voice_id: string
  voice_model?:
    | "eleven_turbo_v2"
    | "eleven_flash_v2"
    | "eleven_turbo_v2_5"
    | "eleven_flash_v2_5"
    | "eleven_multilingual_v2"
    | "eleven_v3"
    | "sonic-2"
    | "sonic-3"
    | "sonic-3-latest"
    | "sonic-turbo"
    | "tts-1"
    | "gpt-4o-mini-tts"
    | "speech-02-turbo"
    | null
  language?:
    | "en-US"
    | "en-IN"
    | "en-GB"
    | "en-AU"
    | "en-NZ"
    | "de-DE"
    | "es-ES"
    | "es-419"
    | "hi-IN"
    | "fr-FR"
    | "fr-CA"
    | "ja-JP"
    | "pt-PT"
    | "pt-BR"
    | "zh-CN"
    | "ru-RU"
    | "it-IT"
    | "ko-KR"
    | "nl-NL"
    | "nl-BE"
    | "pl-PL"
    | "tr-TR"
    | "vi-VN"
    | "ro-RU"
    | "bg-BG"
    | "ca-ES"
    | "th-TH"
    | "da-DK"
    | "fi-FI"
    | "el-GR"
    | "hu-HU"
    | "id-ID"
    | "no-NO"
    | "sk-SK"
    | "sv-SE"
    | "lt-LT"
    | "lv-LV"
    | "ms-MY"
    | "af-ZA"
    | "ar-SA"
    | "az-AZ"
    | "bs-BA"
    | "cy-GB"
    | "fa-IR"
    | "fil-PH"
    | "gl-ES"
    | "he-IL"
    | "hr-HR"
    | "hy-AM"
    | "is-IS"
    | "kk-KZ"
    | "kn-IN"
    | "mk-MK"
    | "mr-IN"
    | "ne-NP"
    | "sl-SI"
    | "sr-RS"
    | "sw-KE"
    | "ta-IN"
    | "ur-IN"
    | "yue-CN"
    | "uk-UA"
    | "multi"

  webhook_url?: string | null
  webhook_timeout_ms?: number

  fallback_voice_ids?: string[] | null

  data_storage_setting?: "everything" | "everything_except_pii" | "basic_attributes_only"
  opt_in_signed_url?: boolean
  signed_url_expiration_ms?: number | null

  // optional convenience fields supported by Retell
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
}

// Update the UpdateAgentParams interface
export interface UpdateAgentParams {
  agent_name?: string | null
  version_description?: string | null
  is_public?: boolean | null

  response_engine?: AgentResponseEngine

  voice_id?: string
  voice_model?:
    | "eleven_turbo_v2"
    | "eleven_flash_v2"
    | "eleven_turbo_v2_5"
    | "eleven_flash_v2_5"
    | "eleven_multilingual_v2"
    | "eleven_v3"
    | "sonic-2"
    | "sonic-3"
    | "sonic-3-latest"
    | "sonic-turbo"
    | "tts-1"
    | "gpt-4o-mini-tts"
    | "speech-02-turbo"
    | null
  language?:
    | "en-US"
    | "en-IN"
    | "en-GB"
    | "en-AU"
    | "en-NZ"
    | "de-DE"
    | "es-ES"
    | "es-419"
    | "hi-IN"
    | "fr-FR"
    | "fr-CA"
    | "ja-JP"
    | "pt-PT"
    | "pt-BR"
    | "zh-CN"
    | "ru-RU"
    | "it-IT"
    | "ko-KR"
    | "nl-NL"
    | "nl-BE"
    | "pl-PL"
    | "tr-TR"
    | "vi-VN"
    | "ro-RU"
    | "bg-BG"
    | "ca-ES"
    | "th-TH"
    | "da-DK"
    | "fi-FI"
    | "el-GR"
    | "hu-HU"
    | "id-ID"
    | "no-NO"
    | "sk-SK"
    | "sv-SE"
    | "lt-LT"
    | "lv-LV"
    | "ms-MY"
    | "af-ZA"
    | "ar-SA"
    | "az-AZ"
    | "bs-BA"
    | "cy-GB"
    | "fa-IR"
    | "fil-PH"
    | "gl-ES"
    | "he-IL"
    | "hr-HR"
    | "hy-AM"
    | "is-IS"
    | "kk-KZ"
    | "kn-IN"
    | "mk-MK"
    | "mr-IN"
    | "ne-NP"
    | "sl-SI"
    | "sr-RS"
    | "sw-KE"
    | "ta-IN"
    | "ur-IN"
    | "yue-CN"
    | "uk-UA"
    | "multi"

  voice_temperature?: number
  voice_speed?: number
  volume?: number
  enable_dynamic_voice_speed?: boolean


  webhook_url?: string | null
  webhook_timeout_ms?: number

  // ✅ exceptional case: stored in your own DB (NOT a Retell field)
  agent_webhook_url?: string | null

  data_storage_setting?: "everything" | "everything_except_pii" | "basic_attributes_only"
  opt_in_signed_url?: boolean
  signed_url_expiration_ms?: number | null

  fallback_voice_ids?: string[] | null

  // Speech settings
  ambient_sound?: "coffee-shop" | "convention-hall" | "summer-outdoor" | "mountain-outdoor" | "static-noise" | "call-center" | null
  ambient_sound_volume?: number
  responsiveness?: number
  interruption_sensitivity?: number
  enable_backchannel?: boolean
  backchannel_frequency?: number
  backchannel_words?: string[] | null
  boosted_keywords?: string[] | null
  normalize_for_speech?: boolean
  reminder_trigger_ms?: number
  reminder_max_count?: number
  pronunciation_dictionary?: Array<{
    word: string
    alphabet: "ipa" | "cmu"
    phoneme: string
  }> | null

  // STT / DTMF / Audio cleanup
  stt_mode?: "fast" | "accurate" | "custom"
  custom_stt_config?: {
    provider: "azure" | "deepgram"
    endpointing_ms: number
  }
  vocab_specialization?: "general" | "medical"
  denoising_mode?: "noise-cancellation" | "noise-and-background-speech-cancellation"
  allow_user_dtmf?: boolean
  user_dtmf_options?: {
    digit_limit?: number | null
    termination_key?: string | null
    timeout_ms?: number
  } | null

  pii_config?: {
    mode: "post_call"
    categories: Array<
      | "person_name"
      | "address"
      | "email"
      | "phone_number"
      | "ssn"
      | "passport"
      | "driver_license"
      | "credit_card"
      | "bank_account"
      | "password"
      | "pin"
      | "medical_id"
      | "date_of_birth"
      | "customer_account_number"
    >
  }

  // Call lifecycle
  begin_message_delay_ms?: number
  ring_duration_ms?: number
  end_call_after_silence_ms?: number
  max_call_duration_ms?: number

  // Voicemail / IVR
  enable_voicemail_detection?: boolean
  voicemail_message?: string
  voicemail_detection_timeout_ms?: number
  voicemail_option?: VoicemailOption | null
  ivr_option?: { action: { type: "hangup" } } | null

  // Post-call analysis
  post_call_analysis_data?: Array<{
    type: "string" | "enum" | "boolean" | "number"
    name: string
    description: string
    examples?: string[]
    choices?: string[]
  }> | null
  post_call_analysis_model?:
    | "gpt-4.1"
    | "gpt-4.1-mini"
    | "gpt-4.1-nano"
    | "gpt-5"
    | "gpt-5.1"
    | "gpt-5.2"
    | "gpt-5-mini"
    | "gpt-5-nano"
    | "claude-4.5-sonnet"
    | "claude-4.5-haiku"
    | "gemini-2.5-flash"
    | "gemini-2.5-flash-lite"
    | "gemini-3.0-flash"
    | null
  analysis_successful_prompt?: string | null
  analysis_summary_prompt?: string | null
}

export interface VoiceDetails {
  voice_id: string
  voice_type: string
  voice_name: string
  provider: "cartesia" | "elevenlabs" | "minimax" | "openai" | "play" | "playht"
  gender: "male" | "female"
  accent?: string
  age?: string
  preview_audio_url?: string
  avatar_url?: string
}

export interface RetellLLM {
  llm_id: string
  last_modification_timestamp: number
  version?: number
  is_published?: boolean

  model:
    | "gpt-4.1"
    | "gpt-4.1-mini"
    | "gpt-4.1-nano"
    | "gpt-5"
    | "gpt-5.1"
    | "gpt-5.2"
    | "gpt-5-mini"
    | "gpt-5-nano"
    | "claude-4.5-sonnet"
    | "claude-4.5-haiku"
    | "gemini-2.5-flash"
    | "gemini-2.5-flash-lite"
    | "gemini-3.0-flash"
    | null

  s2s_model: "gpt-4o-realtime" | "gpt-4o-mini-realtime" | "gpt-realtime" | "gpt-realtime-mini" | null

  model_temperature?: number
  model_high_priority?: boolean | null
  tool_call_strict_mode?: boolean | null

  knowledge_base_ids?: string[] | null
  kb_config?: {
    top_k?: number
    filter_score?: number
  } | null

  start_speaker?: "user" | "agent"
  begin_after_user_silence_ms?: number | null
  begin_message?: string | null

  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null

  default_dynamic_variables?: Record<string, string> | null
  mcps?: Array<{
    name: string
    url: string
    headers?: Record<string, string>
    query_params?: Record<string, string>
    timeout_ms?: number
  }> | null
}

export interface CreateRetellLLMParams {
  model?:
    | "gpt-4.1"
    | "gpt-4.1-mini"
    | "gpt-4.1-nano"
    | "gpt-5"
    | "gpt-5.1"
    | "gpt-5.2"
    | "gpt-5-mini"
    | "gpt-5-nano"
    | "claude-4.5-sonnet"
    | "claude-4.5-haiku"
    | "gemini-2.5-flash"
    | "gemini-2.5-flash-lite"
    | "gemini-3.0-flash"
    | null
  s2s_model?: "gpt-4o-realtime" | "gpt-4o-mini-realtime" | "gpt-realtime" | "gpt-realtime-mini" | null
  model_temperature?: number
  model_high_priority?: boolean | null
  tool_call_strict_mode?: boolean | null
  knowledge_base_ids?: string[] | null
  kb_config?: {
    top_k?: number
    filter_score?: number
  } | null
  start_speaker?: "user" | "agent"
  begin_after_user_silence_ms?: number | null
  begin_message?: string | null
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  default_dynamic_variables?: Record<string, string> | null
  mcps?: Array<{
    name: string
    url: string
    headers?: Record<string, string>
    query_params?: Record<string, string>
    timeout_ms?: number
  }> | null
}

export interface UpdateRetellLLMParams {
  model?:
    | "gpt-4.1"
    | "gpt-4.1-mini"
    | "gpt-4.1-nano"
    | "gpt-5"
    | "gpt-5.1"
    | "gpt-5.2"
    | "gpt-5-mini"
    | "gpt-5-nano"
    | "claude-4.5-sonnet"
    | "claude-4.5-haiku"
    | "gemini-2.5-flash"
    | "gemini-2.5-flash-lite"
    | "gemini-3.0-flash"
    | null
  s2s_model?: "gpt-4o-realtime" | "gpt-4o-mini-realtime" | "gpt-realtime" | "gpt-realtime-mini" | null
  model_temperature?: number
  model_high_priority?: boolean | null
  tool_call_strict_mode?: boolean | null
  knowledge_base_ids?: string[] | null
  kb_config?: {
    top_k?: number
    filter_score?: number
  } | null
  start_speaker?: "user" | "agent"
  begin_after_user_silence_ms?: number | null
  begin_message?: string | null
  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null
  default_dynamic_variables?: Record<string, string> | null
  version?: number | null
  mcps?: Array<{
    name: string
    url: string
    headers?: Record<string, string>
    query_params?: Record<string, string>
    timeout_ms?: number
  }> | null
}

export interface LLMData {
  llm_id: string
  last_modification_timestamp: number
  version?: number
  is_published?: boolean

  model: string | null
  s2s_model: string | null

  model_temperature?: number
  model_high_priority?: boolean | null
  tool_call_strict_mode?: boolean | null

  knowledge_base_ids?: string[] | null
  kb_config?: {
    top_k?: number
    filter_score?: number
  } | null

  start_speaker?: "user" | "agent"
  begin_after_user_silence_ms?: number | null
  begin_message?: string | null

  general_prompt?: string | null
  general_tools?: any[] | null
  states?: any[] | null
  starting_state?: string | null

  default_dynamic_variables?: Record<string, string> | null
  mcps?: Array<{
    name: string
    url: string
    headers?: Record<string, string>
    query_params?: Record<string, string>
    timeout_ms?: number
  }> | null
}

/* ----------------------------------------------------------
 *      agentService with all endpoints
 * --------------------------------------------------------*/

export const agentService = {
  // ---------------- Agent Endpoints ----------------
  list: async (companyName?: string) => {
    const url = companyName ? `/agents?company_name=${encodeURIComponent(companyName)}` : "/agents"
    const response = await api.get<Agent[]>(url)
    return response.data
  },

  get: async (agentId: string) => {
    const response = await api.get<Agent>(`/agents/${agentId}`)
    return response.data
  },

  create: async (params: CreateAgentParams) => {
    const response = await api.post<Agent>("/agents", params)
    return response.data
  },

  delete: async (agentId: string) => {
    await api.delete(`/agents/${agentId}`)
  },

  update: async (agentId: string, params: UpdateAgentParams) => {
    const response = await api.patch<Agent>(`/agents/${agentId}`, params)
    return response.data
  },

  // ---------------- Voice Endpoints ----------------
  getVoiceDetails: async (voiceId: string) => {
    const response = await api.get<VoiceDetails>(`/voices/${voiceId}`)
    return response.data
  },

  listVoices: async () => {
    const response = await api.get<VoiceDetails[]>("/voices")
    return response.data
  },

  // ---------------- LLM Endpoints ----------------
  listRetellLLMs: async () => {
    const response = await api.get<RetellLLM[]>("/llms")
    return response.data
  },

  getRetellLLM: async (llmId: string) => {
    const response = await api.get<RetellLLM>(`/llms/${llmId}`)
    return response.data
  },

  createRetellLLM: async (params: CreateRetellLLMParams) => {
    const response = await api.post<RetellLLM>("/llms", params)
    return response.data
  },

  updateRetellLLM: async (llmId: string, params: UpdateRetellLLMParams) => {
    // Transform the data if general_tools is present
    if (params.general_tools && Array.isArray(params.general_tools)) {
      params.general_tools = params.general_tools.map((tool) => {
        // Handle calendar availability tool
        if (tool.type === "check_availability_cal") {
          // Convert cal_event_type_id to event_type_id if present
          if (tool.cal_event_type_id && !tool.event_type_id) {
            tool.event_type_id = Number(tool.cal_event_type_id)
            delete tool.cal_event_type_id
          }
          // Ensure event_type_id is a number
          if (typeof tool.event_type_id === "string") {
            tool.event_type_id = Number(tool.event_type_id)
          }
        }

        // Handle book appointment tool
        if (tool.type === "book_appointment_cal") {
          // Convert cal_event_type_id to event_type_id if present
          if (tool.cal_event_type_id && !tool.event_type_id) {
            tool.event_type_id = Number(tool.cal_event_type_id)
            delete tool.cal_event_type_id
          }
          // Ensure event_type_id is a number
          if (typeof tool.event_type_id === "string") {
            tool.event_type_id = Number(tool.event_type_id)
          }
        }

        return tool
      })
    }

    try {
      const response = await api.patch<{ success: boolean; data: RetellLLM }>(`/llms/${llmId}`, params)

      if (!response.data.success) {
        throw new Error("Update failed")
      }

      return response.data.data
    } catch (error: any) {
      // Re-throw with the error structure preserved
      if (error.response?.data?.error === "cal_com_validation_error") {
        const calError = new Error(error.response.data.message) as any
        calError.isCalComValidationError = true
        calError.details = error.response.data.details
        throw calError
      }
      throw error
    }
  },

  getLLMData: async (llmId: string): Promise<RetellLLM> => {
    const response = await api.get<RetellLLM>(`/llms/${llmId}`)
    return response.data
  },

  getAgentDetails: async (agentId: string) => {
    const response = await api.get<Agent>(`/agents/${agentId}`)
    return response.data
  },

  updateAgentSettings: async (agentId: string, settings: Partial<Agent>) => {
    const cleanSettings = Object.entries(settings).reduce(
      (acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = value
        }
        return acc
      },
      {} as Record<string, any>,
    )

    const response = await api.patch<Agent>(`/agents/${agentId}`, cleanSettings)
    return response.data
  },
}

// Define the structure for calendar availability tool
export interface CalendarAvailabilityTool {
  type: "check_availability_cal"
  name: string
  description?: string
  cal_api_key: string
  event_type_id: number
  timezone?: string
}

// Define the structure for book appointment tool
export interface BookAppointmentTool {
  type: "book_appointment_cal"
  name: string
  description?: string
  cal_api_key: string
  event_type_id: number
  timezone?: string
}

// Define the union type for all possible tool types
export type GeneralTool = CalendarAvailabilityTool | BookAppointmentTool | any // 'any' for other tool types

export interface FunctionConfig {
  type: string
  name: string
  description?: string
  [key: string]: any
}

export type VoicemailAction =
  | { type: "hangup" }
  | { type: "static_text"; text: string }
  | { type: "prompt"; text: string }
  | { type: "bridge_transfer" }

export interface VoicemailOption {
  action: VoicemailAction
}
